import * as Popover from '@radix-ui/react-popover'
import { useNavigate } from '@tanstack/react-router'
import { AlertTriangle, BellRing, Ban, Check, CircleSlash, Copy, CreditCard, MailCheck, MoreHorizontal, PauseCircle, PlayCircle, RotateCcw, Send, Trash2, Undo2, X } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Money } from '@/components/ui/Money'
import { ReasonModal } from '@/components/ui/ReasonModal'
import { cn } from '@/lib/utils'
import { toNumber } from '@/lib/decimal'
import { useCorrectOrCredit, useDeleteInvoice, useInvoiceAction } from '@/lib/invoice-queries'
import type { Invoice } from '@/lib/invoice-types'
import { Toggle } from '@/components/ui/Toggle'
import { todayIsoDate } from '@/lib/week'

type OpenModal =
  | null
  | 'send'
  | 'reminder'
  | 'reject'
  | 'payment'
  | 'void'
  | 'unapprove'
  | 'correct'
  | 'credit'
  | 'approve'
  | 'park'
  | 'delete'
  | 'escalate'
  | 'withdraw'
  | 'writeOff'

export function ActionBar({ invoice }: { invoice: Invoice }) {
  const navigate = useNavigate()
  const actions = useInvoiceAction(invoice.id)
  const correctCredit = useCorrectOrCredit(invoice.id)
  const removeInvoice = useDeleteInvoice(invoice.id)
  const [modal, setModal] = useState<OpenModal>(null)
  const [menuOpen, setMenuOpen] = useState(false)

  const close = () => setModal(null)

  function goToNew(inv: Invoice) {
    navigate({ to: '/facturen/$id', params: { id: inv.id } })
  }

  const outstanding = invoice.status === 'SENT' || invoice.status === 'ESCALATED'
  const canCorrect = outstanding || invoice.status === 'PAID'
  const canCredit = canCorrect || invoice.status === 'WRITTEN_OFF'
  const canVoid = invoice.status === 'APPROVED'
  const canUnapprove = invoice.status === 'APPROVED'
  const canPark = invoice.status === 'DRAFT'
  const canDelete = invoice.status === 'DRAFT' || invoice.status === 'PARKED'
  const canEscalate = invoice.status === 'SENT'
  const canDeEscalate = invoice.status === 'ESCALATED'
  const showMenu = canCorrect || canCredit || canVoid || canUnapprove || canPark || canDelete || outstanding || canDeEscalate

  return (
    <>
      <div className="sticky bottom-0 -mx-4 flex items-center justify-between gap-2 border-t border-hairline bg-canvas/85 px-4 py-3 backdrop-blur-xl lg:-mx-8 lg:px-8">
        <div className="flex items-baseline gap-1 text-sm text-ink-faint">
          <Money
            amount={toNumber(invoice.subtotal)}
            secondaryAmount={toNumber(invoice.total)}
            className="font-semibold text-ink"
          />
          {invoice.vatReversed ? <span>· BTW verlegd</span> : null}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {showMenu ? (
            <Popover.Root open={menuOpen} onOpenChange={setMenuOpen}>
              <Popover.Trigger asChild>
                <button type="button" className="btn-secondary h-9 w-9 p-0" aria-label="Meer acties">
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Content align="end" sideOffset={6} className="z-40 w-72 rounded-card border border-hairline bg-surface p-1.5 shadow-pop animate-fade-in">
                  {canCorrect ? (
                    <MenuItem
                      icon={Copy}
                      label="Corrigeer"
                      hint="Inhoud klopt niet: creditnota plus nieuw concept"
                      onClick={() => { setMenuOpen(false); setModal('correct') }}
                    />
                  ) : null}
                  {canCredit ? (
                    <MenuItem
                      icon={CreditCard}
                      label="Crediteer"
                      hint="Draait het bedrag terug, factuur blijft staan"
                      onClick={() => { setMenuOpen(false); setModal('credit') }}
                    />
                  ) : null}
                  {canEscalate ? (
                    <MenuItem
                      icon={AlertTriangle}
                      label="Escaleren"
                      hint="Je gaat aanmanen; telt nog mee in openstaand"
                      onClick={() => { setMenuOpen(false); setModal('escalate') }}
                    />
                  ) : null}
                  {canDeEscalate ? (
                    <MenuItem
                      icon={Undo2}
                      label="Escalatie terugdraaien"
                      hint="Klant heeft gereageerd, maar nog niet betaald"
                      onClick={() => { setMenuOpen(false); actions.deEscalate.mutate() }}
                    />
                  ) : null}
                  {canPark ? (
                    <MenuItem
                      icon={PauseCircle}
                      label="Parkeer"
                      hint="Klaarzetten voor later, buiten de maandflow"
                      onClick={() => { setMenuOpen(false); setModal('park') }}
                    />
                  ) : null}
                  {canUnapprove ? (
                    <MenuItem
                      icon={Undo2}
                      label="Goedkeuring intrekken"
                      hint="Terug naar concept; het nummer blijft gereserveerd"
                      onClick={() => { setMenuOpen(false); setModal('unapprove') }}
                    />
                  ) : null}
                  {outstanding ? (
                    <MenuItem
                      icon={Ban}
                      label="Intrekken"
                      hint="Klant heeft hem al; maakt een creditnota"
                      danger
                      onClick={() => { setMenuOpen(false); setModal('withdraw') }}
                    />
                  ) : null}
                  {outstanding ? (
                    <MenuItem
                      icon={CircleSlash}
                      label="Oninbaar boeken"
                      hint="Klant betaalt niet; blijft in de historie"
                      danger
                      onClick={() => { setMenuOpen(false); setModal('writeOff') }}
                    />
                  ) : null}
                  {canVoid ? (
                    <MenuItem
                      icon={CircleSlash}
                      label="Storneer"
                      hint="Nog niet verstuurd, geen creditnota nodig"
                      danger
                      onClick={() => { setMenuOpen(false); setModal('void') }}
                    />
                  ) : null}
                  {canDelete ? (
                    <MenuItem
                      icon={Trash2}
                      label="Verwijder concept"
                      hint="Onomkeerbaar; alleen bij concept of geparkeerd"
                      danger
                      onClick={() => { setMenuOpen(false); setModal('delete') }}
                    />
                  ) : null}
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
          ) : null}

          {invoice.status === 'DRAFT' ? (
            <button type="button" className="btn-primary" onClick={() => actions.submit.mutate()} disabled={actions.submit.isPending}>
              <Check className="h-4 w-4" />
              Ter approval
            </button>
          ) : null}

          {invoice.status === 'PARKED' ? (
            <button type="button" className="btn-primary" onClick={() => actions.release.mutate()} disabled={actions.release.isPending}>
              <PlayCircle className="h-4 w-4" />
              Vrijgeven naar concept
            </button>
          ) : null}

          {invoice.status === 'PENDING_APPROVAL' ? (
            <>
              <button type="button" className="btn-secondary" onClick={() => setModal('reject')}>
                <X className="h-4 w-4" />
                Afkeuren
              </button>
              <button type="button" className="btn-primary" onClick={() => setModal('approve')} disabled={actions.approve.isPending}>
                <Check className="h-4 w-4" />
                Goedkeuren
              </button>
            </>
          ) : null}

          {invoice.status === 'APPROVED' ? (
            <>
              <button type="button" className="btn-secondary" onClick={() => actions.markSent.mutate()} disabled={actions.markSent.isPending}>
                <MailCheck className="h-4 w-4" />
                Markeer verzonden
              </button>
              <button type="button" className="btn-primary" onClick={() => setModal('send')}>
                <Send className="h-4 w-4" />
                Verstuur
              </button>
            </>
          ) : null}

          {outstanding ? (
            <>
              <button type="button" className="btn-secondary" onClick={() => setModal('reminder')}>
                <BellRing className="h-4 w-4" />
                Stuur herinnering
              </button>
              <button type="button" className="btn-primary" onClick={() => setModal('payment')}>
                <CreditCard className="h-4 w-4" />
                Markeer betaald
              </button>
            </>
          ) : null}

          {invoice.status === 'WRITTEN_OFF' ? (
            <button type="button" className="btn-primary" onClick={() => actions.reopen.mutate()} disabled={actions.reopen.isPending}>
              <RotateCcw className="h-4 w-4" />
              Heropenen
            </button>
          ) : null}
        </div>
      </div>

      {modal === 'send' ? <SendModal invoice={invoice} onClose={close} /> : null}
      {modal === 'reminder' ? <ReminderModal invoice={invoice} onClose={close} /> : null}
      {modal === 'reject' ? (
        <ReasonModal
          title="Factuur afkeuren"
          description="Een reden is verplicht en wordt als comment vastgelegd. De factuur gaat terug naar concept."
          confirmLabel="Afkeuren"
          placeholder="Waarom wordt deze factuur afgekeurd?"
          rows={4}
          pending={actions.reject.isPending}
          onClose={close}
          onConfirm={(comment) => actions.reject.mutate(comment, { onSuccess: close })}
        />
      ) : null}
      {modal === 'approve' ? (
        <ApproveModal
          pending={actions.approve.isPending}
          onClose={close}
          onConfirm={(issueDate) => actions.approve.mutate(issueDate, { onSuccess: close })}
        />
      ) : null}
      {modal === 'payment' ? (
        <PaymentModal
          pending={actions.registerPayment.isPending}
          onClose={close}
          onConfirm={(paidAt, note) => actions.registerPayment.mutate({ paidAt, note }, { onSuccess: close })}
        />
      ) : null}
      {modal === 'void' ? (
        <ReasonModal
          title="Factuur storneren"
          description="De factuur is goedgekeurd maar nog niet verstuurd, dus de klant heeft hem nooit gehad en er is geen creditnota nodig. Definitief: het factuurnummer blijft gereserveerd maar de factuur verdwijnt uit de actieve stroom."
          confirmLabel="Storneer"
          placeholder="Reden van storneren"
          danger
          pending={actions.voidInvoice.isPending}
          onClose={close}
          onConfirm={(reason) => actions.voidInvoice.mutate(reason, { onSuccess: close })}
        />
      ) : null}
      {modal === 'escalate' ? (
        <ReasonModal
          title="Factuur escaleren"
          description="Leg vast dat je serieus achter dit geld aan gaat. Geen eindstatus: de factuur blijft meetellen in openstaand, je kunt nog herinneringen sturen en hem gewoon op betaald zetten. Terugdraaien kan altijd."
          confirmLabel="Escaleren"
          placeholder="Bijv. derde herinnering zonder reactie"
          pending={actions.escalate.isPending}
          onClose={close}
          onConfirm={(reason) => actions.escalate.mutate(reason, { onSuccess: close })}
        />
      ) : null}
      {modal === 'writeOff' ? (
        <ReasonModal
          title="Factuur oninbaar boeken"
          description="De klant hoort te betalen maar doet het niet. De factuur blijft in de historie staan, maar valt uit openstaand en uit de omzet, en nog openstaande uitbetalingen aan mede-ondernemers vervallen. Betaalt hij later alsnog, dan kun je hem heropenen."
          confirmLabel="Oninbaar boeken"
          placeholder="Bijv. klant failliet verklaard"
          danger
          pending={actions.writeOff.isPending}
          onClose={close}
          onConfirm={(reason) => actions.writeOff.mutate(reason, { onSuccess: close })}
        />
      ) : null}
      {modal === 'withdraw' ? (
        <WithdrawModal
          pending={actions.withdraw.isPending}
          onClose={close}
          onConfirm={(reason, createCredit) =>
            actions.withdraw.mutate(
              { reason, createCredit },
              {
                onSuccess: (result) => {
                  close()
                  if (result.credit) goToNew(result.credit)
                },
              },
            )
          }
        />
      ) : null}
      {modal === 'park' ? (
        <ParkModal
          pending={actions.park.isPending}
          onClose={close}
          onConfirm={(releaseDate, reason) => actions.park.mutate({ releaseDate, reason }, { onSuccess: close })}
        />
      ) : null}
      {modal === 'delete' ? (
        <ConfirmModal
          title="Concept verwijderen"
          description="Het concept verdwijnt definitief. De uren en reiskosten die erin zaten komen weer vrij en kunnen opnieuw gefactureerd worden."
          confirmLabel="Verwijder"
          icon={<Trash2 className="h-4 w-4" />}
          danger
          pending={removeInvoice.isPending}
          onClose={close}
          onConfirm={() =>
            removeInvoice.mutate(undefined, {
              onSuccess: () => { close(); navigate({ to: '/facturen' }) },
            })
          }
        />
      ) : null}
      {modal === 'unapprove' ? (
        <ReasonModal
          title="Goedkeuring intrekken"
          description="De factuur gaat terug naar concept. Het factuurnummer blijft behouden en wordt bij opnieuw goedkeuren hergebruikt."
          confirmLabel="Intrekken"
          placeholder="Reden van intrekken"
          icon={<Undo2 className="h-4 w-4" />}
          pending={actions.unapprove.isPending}
          onClose={close}
          onConfirm={(reason) => actions.unapprove.mutate(reason, { onSuccess: close })}
        />
      ) : null}
      {modal === 'correct' ? (
        <ConfirmModal
          title="Corrigeer factuur"
          description="Dit maakt een creditnota-concept dat deze factuur terugdraait EN een nieuw concept met de gecorrigeerde inhoud. Je controleert en verstuurt beide."
          confirmLabel="Corrigeer"
          pending={correctCredit.correct.isPending}
          onClose={close}
          onConfirm={() =>
            correctCredit.correct.mutate(undefined, {
              onSuccess: (result) => { correctCredit.onCorrection(result); close(); goToNew(result.draft) },
            })
          }
        />
      ) : null}
      {modal === 'credit' ? (
        <ConfirmModal
          title="Crediteer factuur"
          description="Dit maakt een creditnota-concept met negatieve bedragen aan."
          confirmLabel="Crediteer"
          pending={correctCredit.credit.isPending}
          onClose={close}
          onConfirm={() =>
            correctCredit.credit.mutate(undefined, {
              onSuccess: (inv) => { correctCredit.onNew(inv, 'Creditnota aangemaakt'); close(); goToNew(inv) },
            })
          }
        />
      ) : null}
    </>
  )
}

/**
 * De hint-regel staat er omdat er vijf manieren zijn om een factuur af te sluiten
 * en dit het moment is waarop je kiest.
 */
function MenuItem({
  icon: Icon,
  label,
  hint,
  onClick,
  danger,
}: {
  icon: typeof Copy
  label: string
  hint?: string
  onClick: () => void
  danger?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-start gap-2.5 rounded-md px-2.5 py-2 text-left text-sm hover:bg-fill-2 ${danger ? 'text-status-overdue' : 'text-ink'}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <span className="min-w-0">
        {label}
        {hint ? <span className="mt-0.5 block text-xs font-normal text-ink-faint">{hint}</span> : null}
      </span>
    </button>
  )
}

function SendModal({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  const { send } = useInvoiceAction(invoice.id)
  const [to, setTo] = useState(invoice.clientEmail ?? '')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Factuur versturen"
      description="Controleer de mail-preview. De factuur wordt als PDF bijgevoegd."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn-primary" onClick={() => send.mutate({ to, subject: subject.trim() || undefined, body: body.trim() || undefined }, { onSuccess: onClose })} disabled={!to.trim() || send.isPending}>
            <Send className="h-4 w-4" />
            Verstuur nu
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Aan</span>
          <input value={to} onChange={(e) => setTo(e.target.value)} className="field" placeholder="klant@voorbeeld.nl" />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Onderwerp</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} className="field" placeholder="Laat leeg voor de standaardtekst" />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Tekst</span>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} className="field resize-none" placeholder="Laat leeg voor de standaardtekst" />
        </label>
        <div className="flex items-center gap-2 rounded-lg border border-hairline bg-fill-1 px-3 py-2 text-sm text-ink-soft">
          <span className="font-medium">Bijlage:</span>
          {invoice.fullNumber ?? 'concept'}.pdf
        </div>
      </div>
    </Modal>
  )
}

/**
 * Intrekken heeft gevolgen die je niet ziet (creditnota, uren, uitbetalingen),
 * dus die staan hier expliciet.
 */
function WithdrawModal({
  onClose,
  onConfirm,
  pending,
}: {
  onClose: () => void
  onConfirm: (reason: string, createCredit: boolean) => void
  pending: boolean
}) {
  const [reason, setReason] = useState('')
  const [createCredit, setCreateCredit] = useState(true)
  const trimmed = reason.trim()

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Factuur intrekken"
      description="Je neemt de factuur terug nadat de klant hem al had: hij hoeft niet te betalen. De factuur valt uit openstaand en uit de wekelijkse vervallen-mail, de onderliggende uren komen weer vrij om opnieuw te factureren, en nog openstaande uitbetalingen aan mede-ondernemers vervallen."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className={cn('btn-primary', 'bg-status-overdue hover:bg-status-overdue/90')}
            onClick={() => onConfirm(trimmed, createCredit)}
            disabled={pending || trimmed === ''}
          >
            Intrekken
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block text-sm text-ink-soft">
          Reden
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Bijv. opdracht geannuleerd na verzending"
            className="field mt-1"
          />
        </label>
        <div className="flex items-start justify-between gap-4 rounded-lg border border-hairline p-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-ink">Creditnota aanmaken</p>
            <p className="mt-0.5 text-xs text-ink-faint">
              {createCredit
                ? 'De klant heeft het factuurnummer in zijn administratie staan, dus die boeking wordt netjes teruggedraaid met een creditnota-concept.'
                : 'Alleen kiezen als de factuur de klant nooit bereikt heeft, bijvoorbeeld doordat de mail bounced. Anders blijft de factuur bij hem openstaan.'}
            </p>
          </div>
          <Toggle checked={createCredit} onChange={setCreateCredit} label="Creditnota aanmaken" />
        </div>
      </div>
    </Modal>
  )
}

function ParkModal({ onClose, onConfirm, pending }: { onClose: () => void; onConfirm: (releaseDate: string | null, reason: string | null) => void; pending: boolean }) {
  const [releaseDate, setReleaseDate] = useState('')
  const [reason, setReason] = useState('')
  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Factuur parkeren"
      description="Een geparkeerde factuur staat buiten de maandelijkse concept-flow en gaat niet mee in bulk-acties. Vrijgeven kan handmatig of automatisch op de vrijgavedatum."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn-primary" onClick={() => onConfirm(releaseDate || null, reason.trim() || null)} disabled={pending}>
            Parkeer
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block text-sm text-ink-soft">
          Vrijgavedatum (optioneel)
          <input type="date" value={releaseDate} onChange={(e) => setReleaseDate(e.target.value)} className="field mt-1" />
        </label>
        <label className="block text-sm text-ink-soft">
          Reden (optioneel)
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Bijv. wacht op oplevering"
            className="field mt-1"
          />
        </label>
      </div>
    </Modal>
  )
}

function ApproveModal({ onClose, onConfirm, pending }: { onClose: () => void; onConfirm: (issueDate: string) => void; pending: boolean }) {
  const [issueDate, setIssueDate] = useState(todayIsoDate())
  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Factuur goedkeuren"
      description="Bij goedkeuren worden het factuurnummer en de PDF bevroren. Kies de factuurdatum; de vervaldatum volgt uit de betaaltermijn."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn-primary" onClick={() => onConfirm(issueDate)} disabled={!issueDate || pending}>
            <Check className="h-4 w-4" />
            Goedkeuren
          </button>
        </>
      }
    >
      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-ink-soft">Factuurdatum</span>
        <input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} className="field" autoFocus />
      </label>
    </Modal>
  )
}

function PaymentModal({ onClose, onConfirm, pending }: { onClose: () => void; onConfirm: (paidAt: string, note?: string) => void; pending: boolean }) {
  const [paidAt, setPaidAt] = useState(todayIsoDate())
  const [note, setNote] = useState('')
  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Betaling registreren"
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn-primary" onClick={() => onConfirm(paidAt, note.trim() || undefined)} disabled={pending}>
            Markeer betaald
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Betaaldatum</span>
          <input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} className="field" />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Notitie (optioneel)</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} className="field" placeholder="Bijv. bankreferentie" />
        </label>
      </div>
    </Modal>
  )
}

function ReminderModal({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  const { sendReminder } = useInvoiceAction(invoice.id)
  const [to, setTo] = useState(invoice.sentTo ?? invoice.clientEmail ?? '')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Betalingsherinnering versturen"
      description="De factuur-PDF wordt opnieuw als bijlage meegestuurd."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => sendReminder.mutate({ to, subject: subject.trim() || undefined, body: body.trim() || undefined }, { onSuccess: onClose })}
            disabled={!to.trim() || sendReminder.isPending}
          >
            <BellRing className="h-4 w-4" />
            Verstuur herinnering
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Aan</span>
          <input value={to} onChange={(e) => setTo(e.target.value)} className="field" placeholder="klant@voorbeeld.nl" />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Onderwerp</span>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} className="field" placeholder="Laat leeg voor de standaardtekst" />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Bericht</span>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={8} className="field resize-none" placeholder="Laat leeg voor de standaardtekst" />
        </label>
      </div>
    </Modal>
  )
}


function ConfirmModal({
  title,
  description,
  confirmLabel,
  icon,
  danger,
  onClose,
  onConfirm,
  pending,
}: {
  title: string
  description: string
  confirmLabel: string
  icon?: ReactNode
  danger?: boolean
  onClose: () => void
  onConfirm: () => void
  pending: boolean
}) {
  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={title}
      description={description}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className={cn('btn-primary', danger && 'bg-status-overdue hover:bg-status-overdue/90')}
            onClick={onConfirm}
            disabled={pending}
          >
            {icon}
            {confirmLabel}
          </button>
        </>
      }
    >
      <p className="text-sm text-ink-soft">Weet je het zeker?</p>
    </Modal>
  )
}
